import React, { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import { cajaAPI, periodosAPI } from '../services/api';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';

const denominaciones = [
  { valor: 100, label: '$100' }, { valor: 50, label: '$50' },
  { valor: 20, label: '$20' }, { valor: 10, label: '$10' },
  { valor: 5, label: '$5' }, { valor: 1, label: '$1' },
  { valor: 0.50, label: '$0.50' }, { valor: 0.25, label: '$0.25' },
  { valor: 0.10, label: '$0.10' }, { valor: 0.05, label: '$0.05' },
  { valor: 0.01, label: '$0.01' }
];

const Caja = () => {
  const { formatCurrency } = useApp();
  const { esAdmin, usuario, modoEspectador } = useAuth();
  const esAdminOGerente = esAdmin() || usuario?.rol === 'gerente';
  const [cajaActiva, setCajaActiva] = useState(null);
  const [historial, setHistorial] = useState([]);
  const [movimientos, setMovimientos] = useState([]);
  const [cajeros, setCajeros] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('activa');
  const [saving, setSaving] = useState(false);

  // Periodo diario
  const [periodoActivo, setPeriodoActivo] = useState(null);
  const [cajasDelPeriodo, setCajasDelPeriodo] = useState([]);
  const [loadingPeriodo, setLoadingPeriodo] = useState(true);
  const [showCerrarPeriodo, setShowCerrarPeriodo] = useState(false);
  const [obsCierrePeriodo, setObsCierrePeriodo] = useState('');
  const [resumenPeriodo, setResumenPeriodo] = useState(null);

  // Modal abrir
  const [showAbrir, setShowAbrir] = useState(false);
  const [abrirForm, setAbrirForm] = useState({
    admin_username: '', admin_password: '',
    cajero_id: '', monto_apertura: '0', observaciones: '',
    fondo_denominaciones: {}
  });
  const [abrirError, setAbrirError] = useState('');
  const [usarDenominaciones, setUsarDenominaciones] = useState(false);

  // Modal cerrar
  const [showCerrar, setShowCerrar] = useState(false);
  const [cerrarForm, setCerrarForm] = useState({
    monto_real: '', observaciones: '', cierre_denominaciones: {}, justificacion_diferencia: '',
    admin_username: '', admin_password: ''
  });
  const [usarDenomCierre, setUsarDenomCierre] = useState(false);
  const [cerrarStep, setCerrarStep] = useState('conteo'); // 'conteo' | 'resultado'

  // Validaciones pre-cierre
  const [showValidaciones, setShowValidaciones] = useState(false);
  const [validaciones, setValidaciones] = useState(null);
  const [loadingValidacion, setLoadingValidacion] = useState(false);

  // Modal movimiento
  const [showMov, setShowMov] = useState(false);
  const [movForm, setMovForm] = useState({ tipo: 'ingreso', concepto: '', monto: '', metodo_pago: 'efectivo' });
  const [movAdjuntos, setMovAdjuntos] = useState([]);
  const movAdjuntosRef = useRef(null);

  // Modal detalle periodo
  const [showDetalle, setShowDetalle] = useState(false);
  const [periodoDetalle, setPeriodoDetalle] = useState(null);
  const [movsPeriodo, setMovsPeriodo] = useState([]);

  // Helpers: detectar cajas zombie (abiertas de un dia anterior)
  const esCajaZombie = (caja) => {
    if (caja.estado !== 'abierta' || !caja.fecha_apertura) return false;
    const apertura = new Date(caja.fecha_apertura);
    const hoy = new Date();
    return apertura.toDateString() !== hoy.toDateString();
  };
  const horasAbierta = (caja) => {
    if (!caja.fecha_apertura) return 0;
    return Math.floor((new Date() - new Date(caja.fecha_apertura)) / 3600000);
  };

  // FIX: Added null checks on API response data and user feedback on error
  const cargarCaja = useCallback(async () => {
    setLoading(true);
    try {
      const res = await cajaAPI.getActiva();
      const cajaData = res?.data?.data || null;
      setCajaActiva(cajaData);
      if (cajaData) {
        setMovimientos(cajaData.movimientos || []);
      } else {
        setMovimientos([]);
      }
    } catch (e) {
      console.error('Error cargando caja activa:', e);
      setCajaActiva(null);
      setMovimientos([]);
      // FIX: Show user feedback on caja load error instead of silent catch
      toast.error('Error al cargar la caja. Intente recargar.');
    }
    finally { setLoading(false); }
  }, []);

  // FIX: Wrapped cargarHistorial in useCallback to stabilize reference; added null checks
  const cargarHistorial = useCallback(async () => {
    try {
      const res = await cajaAPI.getHistorial({ limit: 30 });
      if (res?.data?.success) setHistorial(res.data.data || []);
    } catch (e) {
      console.error('Error cargando historial:', e);
      setHistorial([]);
    }
  }, []);

  // FIX: Wrapped cargarCajeros in useCallback; added null checks on response
  const cargarCajeros = useCallback(async () => {
    try {
      const res = await cajaAPI.getCajeros();
      if (res?.data?.success) setCajeros(res.data.data || []);
    } catch (e) {
      console.error('Error cargando cajeros:', e);
      setCajeros([]);
    }
  }, []);

  // PERIODO DIARIO
  // FIX: Added null checks on API response data
  const cargarPeriodo = useCallback(async () => {
    setLoadingPeriodo(true);
    try {
      const res = await periodosAPI.getActivo();
      if (res?.data?.success && res.data.data) {
        setPeriodoActivo(res.data.data);
        setCajasDelPeriodo(res.data.data.cajas || []);
      } else {
        setPeriodoActivo(null);
        setCajasDelPeriodo([]);
      }
    } catch (e) {
      console.error('Error cargando periodo:', e);
      setPeriodoActivo(null);
      setCajasDelPeriodo([]);
    }
    finally { setLoadingPeriodo(false); }
  }, []);

  // FIX: Added null check on error response and refresh caja/historial after opening period
  const handleAbrirPeriodo = async () => {
    setSaving(true);
    try {
      await periodosAPI.abrir({});
      toast.success('Periodo del dia abierto exitosamente');
      cargarPeriodo();
      cargarCaja();
    } catch (e) {
      const errorMsg = e?.response?.data?.error || e?.message || 'Error al abrir periodo';
      toast.error(errorMsg);
    } finally { setSaving(false); }
  };

  // FIX: Added null checks on API response data
  const handleCerrarPeriodo = async () => {
    setSaving(true);
    try {
      const res = await periodosAPI.cerrar({ observaciones: obsCierrePeriodo || null });
      if (res?.data?.success) {
        setResumenPeriodo(res.data.data || null);
        toast.success('Periodo cerrado exitosamente');
      }
      setShowCerrarPeriodo(false);
      // FIX: Refresh all UI data after cerrar periodo
      cargarPeriodo();
      cargarCaja();
      cargarHistorial();
    } catch (e) {
      const errorMsg = e?.response?.data?.error || e?.message || 'Error al cerrar periodo';
      toast.error(errorMsg);
      // FIX: Safe access on cajas_abiertas array
      const cajasAbiertas = e?.response?.data?.cajas_abiertas;
      if (Array.isArray(cajasAbiertas) && cajasAbiertas.length > 0) {
        toast.warning(`Cajas aun abiertas: ${cajasAbiertas.map(c => c?.cajero_nombre || 'Desconocido').join(', ')}`);
      }
    } finally { setSaving(false); }
  };

  // FIX: Complete dependency array — added cargarHistorial to prevent stale closure
  useEffect(() => { cargarCaja(); cargarHistorial(); cargarPeriodo(); }, [cargarCaja, cargarHistorial, cargarPeriodo]);

  // Calcular total de denominaciones
  const calcTotalDenom = (denoms) => {
    return denominaciones.reduce((sum, d) => {
      const cant = parseInt(denoms[d.valor] || 0);
      return sum + (cant * d.valor);
    }, 0);
  };

  // ABRIR CAJA
  const openAbrirModal = () => {
    setAbrirForm({
      admin_username: '', admin_password: '',
      cajero_id: usuario?.id?.toString() || '',
      monto_apertura: '0', observaciones: '',
      fondo_denominaciones: {}
    });
    setAbrirError('');
    setUsarDenominaciones(false);
    cargarCajeros();
    setShowAbrir(true);
  };

  const handleAbrir = async () => {
    setAbrirError('');
    const monto = usarDenominaciones
      ? calcTotalDenom(abrirForm.fondo_denominaciones)
      : parseFloat(abrirForm.monto_apertura || 0);

    if (monto < 0) {
      setAbrirError('El monto no puede ser negativo');
      return;
    }

    // Validar credenciales admin si no es admin/gerente el que abre
    if (!esAdminOGerente && (!abrirForm.admin_username || !abrirForm.admin_password)) {
      setAbrirError('Se requiere autorización de un Administrador o Gerente para abrir la caja');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        monto_apertura: monto,
        observaciones: abrirForm.observaciones || null,
        cajero_id: abrirForm.cajero_id ? parseInt(abrirForm.cajero_id) : undefined,
        fondo_denominaciones: usarDenominaciones ? abrirForm.fondo_denominaciones : undefined
      };

      // Si NO es admin/gerente, enviar credenciales de autorización
      if (!esAdminOGerente) {
        payload.admin_username = abrirForm.admin_username;
        payload.admin_password = abrirForm.admin_password;
      } else {
        // Si ES admin/gerente, auto-autorizar con sus propias credenciales
        // No necesita enviar contraseña, el backend lo registra automáticamente
      }

      // FIX: Added null check on API response
      const res = await cajaAPI.abrir(payload);
      if (res?.data?.success !== false) {
        toast.success('Caja abierta exitosamente');
        setShowAbrir(false);
        // FIX: Refresh all UI data after abrir operation
        cargarCaja();
        cargarHistorial();
        cargarPeriodo();
      } else {
        setAbrirError(res?.data?.error || 'Error desconocido al abrir caja');
      }
    } catch (e) {
      // FIX: Detailed error feedback from server response
      const errorMsg = e?.response?.data?.error || e?.message || 'Error al abrir caja';
      setAbrirError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  // CERRAR CAJA — Primero validar, luego mostrar modal
  // FIX: Added null checks on validation API response
  const openCerrarModal = async () => {
    setLoadingValidacion(true);
    try {
      const res = await cajaAPI.validarCierre();
      if (res?.data?.success) {
        const val = res.data.data || {};
        if (!val.puede_cerrar) {
          // Hay problemas que bloquean el cierre
          setValidaciones(val);
          setShowValidaciones(true);
          setLoadingValidacion(false);
          return;
        }
        // Si tiene advertencias de stock pero puede cerrar, guardar para mostrar
        if (val.validaciones?.stock_vs_ventas && !val.validaciones.stock_vs_ventas.ok) {
          setValidaciones(val);
        } else {
          setValidaciones(null);
        }
      }
    } catch (e) {
      // Si falla la validación, continuar de todas formas (backward compatible)
      console.warn('Error al validar cierre:', e);
    }
    setLoadingValidacion(false);
    setCerrarForm({ monto_real: '', observaciones: '', cierre_denominaciones: {}, justificacion_diferencia: '', admin_username: '', admin_password: '' });
    setUsarDenomCierre(false);
    setCerrarStep('conteo');
    setShowCerrar(true);
  };

  // EFECTIVO esperado en el cajón: lo calcula el backend con la MISMA fórmula del cierre
  // (caja.controller → calcularEfectivoEsperado): apertura + ingresos en efectivo - retiros/egresos
  // en efectivo. Tarjeta y transferencia NO están en el cajón, y el movimiento 'fondo' es el fondo
  // de apertura (ya incluido en monto_apertura) → no se suma otra vez (auditoría 2026-10-18).
  const movTotales = cajaActiva?.movimientos_totales || {};
  const saldoEsperado = cajaActiva ? (parseFloat(cajaActiva.efectivo_esperado) || 0) : 0;
  const cobrosEfectivo = parseFloat(movTotales.ingresos_efectivo || 0);
  const cobrosNoEfectivo = parseFloat(movTotales.ingresos_no_efectivo || 0);

  const getMontoContado = () => {
    return usarDenomCierre
      ? calcTotalDenom(cerrarForm.cierre_denominaciones)
      : parseFloat(cerrarForm.monto_real || 0);
  };

  const handleCerrarStep1 = () => {
    const montoReal = getMontoContado();
    if (montoReal < 0) {
      toast.error('El monto no puede ser negativo');
      return;
    }
    if (!montoReal) {
      toast.error('Ingrese el conteo de efectivo');
      return;
    }
    // Avanzar al paso de resultado — revelar diferencia
    setCerrarStep('resultado');
  };

  const handleCerrar = async () => {
    const montoReal = getMontoContado();
    const diff = montoReal - saldoEsperado;

    // Validar justificación si la diferencia es > $2
    if (Math.abs(diff) > 2 && !cerrarForm.justificacion_diferencia.trim()) {
      toast.error('La diferencia es mayor a $2.00 — debe ingresar una justificación');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        monto_real: montoReal,
        observaciones: cerrarForm.observaciones,
        cierre_denominaciones: usarDenomCierre ? cerrarForm.cierre_denominaciones : undefined,
        justificacion_diferencia: cerrarForm.justificacion_diferencia || undefined
      };
      // Si no es admin/super_admin, enviar credenciales de autorización
      if (!esAdmin()) {
        if (!cerrarForm.admin_username || !cerrarForm.admin_password) {
          toast.error('Se requieren credenciales de un Administrador para cerrar la caja');
          setSaving(false);
          return;
        }
        payload.admin_username = cerrarForm.admin_username;
        payload.admin_password = cerrarForm.admin_password;
      }
      await cajaAPI.cerrar(payload);
      toast.success('Caja cerrada exitosamente');
      setShowCerrar(false);
      // FIX: Refresh all UI data after cerrar operation (including periodo)
      cargarCaja();
      cargarHistorial();
      cargarPeriodo();
    } catch (e) {
      // FIX: Detailed error feedback from server response
      const errorMsg = e?.response?.data?.error || e?.message || 'Error al cerrar caja';
      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  // ADJUNTOS MOVIMIENTO
  const handleMovAdjuntosChange = (e) => {
    const files = Array.from(e.target.files);
    const allowed = ['image/jpeg','image/png','image/gif','image/webp','application/pdf'];
    const valid = files.filter(f => allowed.includes(f.type));
    if (files.length - valid.length > 0) toast.warning('Solo se permiten imágenes y PDF');
    setMovAdjuntos(prev => {
      const combined = [...prev, ...valid];
      if (combined.length > 6) { toast.warning('Máximo 6 archivos'); return combined.slice(0, 6); }
      return combined;
    });
    if (movAdjuntosRef.current) movAdjuntosRef.current.value = '';
  };

  // MOVIMIENTO
  // FIX: Added null checks on API response and refresh periodo after movimiento
  const handleMovimiento = async () => {
    if (!movForm.concepto?.trim() || !movForm.monto || parseFloat(movForm.monto) <= 0) {
      return toast.warning('Motivo y monto son obligatorios');
    }
    if (movForm.concepto.trim().length < 5) {
      return toast.warning('El motivo debe tener al menos 5 caracteres');
    }
    setSaving(true);
    try {
      const res = await cajaAPI.registrarMovimiento(movForm);
      // Si hay adjuntos, subirlos como comprobantes del movimiento
      if (movAdjuntos.length > 0 && res?.data?.data?.id) {
        try {
          await cajaAPI.uploadMovimientoComprobante(res.data.data.id, movAdjuntos);
        } catch (upErr) {
          console.warn('Error subiendo comprobantes de movimiento:', upErr);
          toast.warning('Movimiento registrado pero hubo un error al subir comprobantes');
        }
      }
      toast.success('Movimiento registrado' + (movAdjuntos.length > 0 ? ` con ${movAdjuntos.length} comprobante(s)` : ''));
      setShowMov(false);
      setMovForm({ tipo: 'ingreso', concepto: '', monto: '', metodo_pago: 'efectivo' });
      setMovAdjuntos([]);
      // FIX: Refresh caja and periodo data after movimiento to update UI
      cargarCaja();
      cargarPeriodo();
    } catch (e) {
      const errorMsg = e?.response?.data?.error || e?.message || 'Error al registrar movimiento';
      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  // DETALLE PERIODO
  // FIX: Added null checks on API response data
  const verDetalle = async (periodo) => {
    if (!periodo?.id) return;
    setPeriodoDetalle(periodo);
    try {
      const res = await cajaAPI.getMovimientos({ periodo_id: periodo.id });
      if (res?.data?.success) setMovsPeriodo(res.data.data || []);
      else setMovsPeriodo([]);
    } catch (e) {
      console.error('Error cargando detalle periodo:', e);
      setMovsPeriodo([]);
    }
    setShowDetalle(true);
  };

  const movIcon = (tipo) => ({
    venta: 'bi-cart-check text-success', ingreso: 'bi-arrow-down-circle text-info',
    fondo: 'bi-safe text-primary', egreso: 'bi-arrow-up-circle text-danger',
    retiro: 'bi-cash-stack text-warning'
  }[tipo] || 'bi-circle');

  // Componente de conteo por denominaciones
  const DenomCounter = ({ values, onChange }) => (
    <div className="row g-2">
      {denominaciones.map(d => (
        <div key={d.valor} className="col-4 col-md-3">
          <div className="input-group input-group-sm">
            <span className="input-group-text" style={{minWidth:'55px', fontSize:'0.8rem'}}>
              {d.label}
            </span>
            <input
              type="number" className="form-control text-center" min="0"
              value={values[d.valor] || ''}
              onChange={e => onChange({...values, [d.valor]: e.target.value})}
              placeholder="0"
            />
          </div>
        </div>
      ))}
      <div className="col-12 mt-2">
        <div className="alert alert-primary py-2 mb-0 text-center">
          <strong>Total Contado: {formatCurrency(calcTotalDenom(values))}</strong>
        </div>
      </div>
    </div>
  );

  if (loading) return (
    <div className="d-flex justify-content-center align-items-center" style={{minHeight:'60vh'}}>
      <div className="text-center">
        <div className="spinner-border text-primary mb-3"></div>
        <p className="text-muted">Cargando caja...</p>
      </div>
    </div>
  );

  return (
    <div className="container-fluid">
      {/* TABS */}
      <ul className="nav nav-pills mb-4">
        {esAdminOGerente && (
          <li className="nav-item">
            <button className={`nav-link ${tab === 'periodo' ? 'active' : ''}`} onClick={() => { setTab('periodo'); cargarPeriodo(); }}>
              <i className="bi bi-calendar-day me-1"></i>Periodo del Dia
              {periodoActivo && <span className="badge bg-success ms-1">{cajasDelPeriodo.filter(c => c.estado === 'abierta').length} abiertas</span>}
            </button>
          </li>
        )}
        <li className="nav-item">
          <button className={`nav-link ${tab === 'activa' ? 'active' : ''}`} onClick={() => setTab('activa')}>
            <i className="bi bi-safe me-1"></i>Mi Caja
          </button>
        </li>
        <li className="nav-item">
          <button className={`nav-link ${tab === 'historial' ? 'active' : ''}`} onClick={() => { setTab('historial'); cargarHistorial(); }}>
            <i className="bi bi-clock-history me-1"></i>Historial
          </button>
        </li>
      </ul>

      {/* ═══ TAB: PERIODO DEL DIA (solo admin/gerente) ═══ */}
      {tab === 'periodo' && esAdminOGerente && (
        <>
          {loadingPeriodo ? (
            <div className="text-center py-5"><div className="spinner-border text-primary"></div></div>
          ) : !periodoActivo ? (
            /* Sin periodo abierto */
            <div className="text-center py-5">
              <div className="mb-4">
                <div className="d-inline-flex align-items-center justify-content-center rounded-circle bg-warning bg-opacity-10" style={{width:120, height:120}}>
                  <i className="bi bi-calendar-x display-3 text-warning"></i>
                </div>
              </div>
              <h3 className="text-muted mb-2">No hay periodo diario abierto</h3>
              <p className="text-muted mb-4">Abra el periodo del dia para que los cajeros puedan empezar a trabajar.</p>
              <button className="btn btn-success btn-lg px-5" onClick={handleAbrirPeriodo} disabled={saving || modoEspectador}>
                {saving ? <span className="spinner-border spinner-border-sm me-2"></span> : <i className="bi bi-play-circle me-2"></i>}
                Abrir Periodo del Dia
              </button>
            </div>
          ) : (
            /* Periodo abierto - dashboard multi-cajero */
            <>
              {/* Banner del periodo */}
              <div className="alert alert-success d-flex align-items-center mb-4">
                <i className="bi bi-calendar-check fs-4 me-3"></i>
                <div className="flex-grow-1">
                  {/* FIX: null safety — guard date construction */}
                  <strong>Periodo Abierto:</strong> {periodoActivo.codigo} — {periodoActivo.fecha_apertura ? new Date(periodoActivo.fecha_apertura).toLocaleDateString('es-EC', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }) : '—'}
                  <div className="small text-muted">
                    Abierto por {periodoActivo.abierto_por_nombre || '—'} a las {periodoActivo.fecha_apertura ? new Date(periodoActivo.fecha_apertura).toLocaleTimeString('es-EC', { hour: '2-digit', minute: '2-digit' }) : '—'}
                  </div>
                </div>
                <button className="btn btn-outline-danger" onClick={() => { setObsCierrePeriodo(''); setShowCerrarPeriodo(true); cargarPeriodo(); }} disabled={modoEspectador}>
                  <i className="bi bi-stop-circle me-1"></i>Cerrar Periodo
                </button>
              </div>

              {/* Stats rapidos */}
              <div className="row g-3 mb-4">
                <div className="col-6 col-md-3">
                  <div className="card border-0 shadow-sm text-center">
                    <div className="card-body py-3">
                      <div className="text-muted small">Cajas Abiertas</div>
                      <h3 className="fw-bold text-success mb-0">{cajasDelPeriodo.filter(c => c.estado === 'abierta').length}</h3>
                    </div>
                  </div>
                </div>
                <div className="col-6 col-md-3">
                  <div className="card border-0 shadow-sm text-center">
                    <div className="card-body py-3">
                      <div className="text-muted small">Cajas Cerradas</div>
                      <h3 className="fw-bold text-secondary mb-0">{cajasDelPeriodo.filter(c => c.estado === 'cerrada').length}</h3>
                    </div>
                  </div>
                </div>
                <div className="col-6 col-md-3">
                  <div className="card border-0 shadow-sm text-center">
                    <div className="card-body py-3">
                      <div className="text-muted small">Total Ventas</div>
                      <h3 className="fw-bold text-primary mb-0">{cajasDelPeriodo.reduce((s, c) => s + parseInt(c.num_ventas || 0), 0)}</h3>
                    </div>
                  </div>
                </div>
                <div className="col-6 col-md-3">
                  <div className="card border-0 shadow-sm text-center">
                    <div className="card-body py-3">
                      <div className="text-muted small">Monto Vendido</div>
                      <h3 className="fw-bold text-success mb-0">{formatCurrency(cajasDelPeriodo.reduce((s, c) => s + parseFloat(c.monto_ventas || 0), 0))}</h3>
                    </div>
                  </div>
                </div>
              </div>

              {/* Alerta de cajas zombie */}
              {cajasDelPeriodo.some(c => esCajaZombie(c)) && (
                <div className="alert alert-danger d-flex align-items-center mb-3">
                  <i className="bi bi-exclamation-triangle-fill fs-4 me-3"></i>
                  <div>
                    <strong>Cajas zombie detectadas:</strong> Hay {cajasDelPeriodo.filter(c => esCajaZombie(c)).length} caja(s) abiertas desde un dia anterior.
                    Deben cerrarse manualmente o seran auto-cerradas a las 21:00.
                  </div>
                </div>
              )}

              {/* Tabla de cajas del periodo */}
              <div className="card border-0 shadow-sm">
                <div className="card-header bg-transparent">
                  <h6 className="mb-0 fw-bold">
                    <i className="bi bi-people me-2"></i>Cajas del Periodo
                    <span className="badge bg-primary ms-2">{cajasDelPeriodo.length}</span>
                  </h6>
                </div>
                <div className="table-responsive">
                  <table className="table table-hover align-middle mb-0">
                    <thead className="bg-light">
                      <tr>
                        <th className="border-0">Cajero</th>
                        <th className="border-0">Codigo</th>
                        <th className="border-0">Apertura</th>
                        <th className="border-0">Cierre</th>
                        <th className="border-0 text-end">Ventas</th>
                        <th className="border-0 text-end">Monto</th>
                        <th className="border-0 text-end" title="Todos los métodos de pago">Ingresos (todos)</th>
                        <th className="border-0 text-end">Retiros</th>
                        <th className="border-0 text-end">Diferencia</th>
                        <th className="border-0">Estado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cajasDelPeriodo.length === 0 ? (
                        <tr><td colSpan="10" className="text-center py-4 text-muted">Ningun cajero ha abierto su caja aun</td></tr>
                      ) : cajasDelPeriodo.map(c => (
                        <tr key={c.id} className={esCajaZombie(c) ? 'table-danger' : ''}>
                          <td className="fw-semibold">
                            <i className="bi bi-person me-1"></i>
                            {c.cajero_asignado_nombre || c.cajero_nombre}
                          </td>
                          <td className="small text-muted">{c.codigo}</td>
                          {/* FIX: null safety — guard date construction */}
                          <td className="small">{c.fecha_apertura ? new Date(c.fecha_apertura).toLocaleTimeString('es-EC', { hour: '2-digit', minute: '2-digit' }) : '—'}</td>
                          <td className="small">{c.fecha_cierre ? new Date(c.fecha_cierre).toLocaleTimeString('es-EC', { hour: '2-digit', minute: '2-digit' }) : '—'}</td>
                          <td className="text-end">{c.num_ventas || 0}</td>
                          <td className="text-end fw-bold text-success">{formatCurrency(c.monto_ventas || 0)}</td>
                          <td className="text-end">{formatCurrency(c.total_ingresos || 0)}</td>
                          <td className="text-end">{formatCurrency(c.total_retiros || 0)}</td>
                          <td className={`text-end fw-bold ${parseFloat(c.diferencia || 0) < 0 ? 'text-danger' : parseFloat(c.diferencia || 0) > 0 ? 'text-warning' : ''}`}>
                            {c.diferencia != null ? formatCurrency(c.diferencia) : '—'}
                          </td>
                          <td>
                            {c.estado === 'abierta' && esCajaZombie(c) ? (
                              <span className="badge bg-danger">
                                <i className="bi bi-exclamation-triangle-fill me-1"></i>Zombie ({horasAbierta(c)}h)
                              </span>
                            ) : (
                              <span className={`badge ${c.estado === 'abierta' ? 'bg-success' : 'bg-secondary'}`}>
                                {c.estado === 'abierta' ? 'Abierta' : 'Cerrada'}
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </>
      )}

      {/* ═══ TAB: MI CAJA ═══ */}
      {tab === 'activa' ? (
        <>
          {!periodoActivo && !loadingPeriodo ? (
            /* Sin periodo diario abierto */
            <div className="text-center py-5">
              <div className="mb-4">
                <div className="d-inline-flex align-items-center justify-content-center rounded-circle bg-warning bg-opacity-10" style={{width:120, height:120}}>
                  <i className="bi bi-calendar-x display-3 text-warning"></i>
                </div>
              </div>
              <h3 className="text-muted mb-2">No hay periodo diario abierto</h3>
              <p className="text-muted mb-4">
                {esAdminOGerente
                  ? 'Vaya a la pestana "Periodo del Dia" para abrir el periodo antes de abrir cajas.'
                  : 'Un administrador o gerente debe abrir el periodo del dia antes de que puedas abrir tu caja.'}
              </p>
              {esAdminOGerente && (
                <button className="btn btn-success btn-lg px-5" onClick={handleAbrirPeriodo} disabled={saving}>
                  {saving ? <span className="spinner-border spinner-border-sm me-2"></span> : <i className="bi bi-play-circle me-2"></i>}
                  Abrir Periodo del Dia
                </button>
              )}
            </div>
          ) : !cajaActiva ? (
            /* Periodo abierto pero sin caja propia */
            <div className="text-center py-5">
              <div className="mb-4">
                <div className="d-inline-flex align-items-center justify-content-center rounded-circle bg-primary bg-opacity-10"
                     style={{width:120, height:120}}>
                  <i className="bi bi-safe display-3 text-primary"></i>
                </div>
              </div>
              <h3 className="text-muted mb-2">No tienes caja abierta</h3>
              <p className="text-muted mb-4">
                {esAdminOGerente
                  ? 'Abre tu caja para comenzar a operar. Otros cajeros pueden abrir sus propias cajas tambien.'
                  : 'Se requiere autorizacion para abrir la caja.'}
              </p>
              {periodoActivo && (
                <small className="d-block text-muted mb-3">
                  <i className="bi bi-calendar-check text-success me-1"></i>
                  Periodo del dia abierto ({periodoActivo.codigo})
                </small>
              )}
              <button className="btn btn-primary btn-lg px-5" onClick={openAbrirModal} disabled={modoEspectador}>
                <i className="bi bi-unlock me-2"></i>{modoEspectador ? 'Modo Espectador' : 'Abrir Mi Caja'}
              </button>
            </div>
          ) : (
            <>
              {/* Alerta si la caja activa es zombie (abierta de un dia anterior) */}
              {cajaActiva && esCajaZombie(cajaActiva) && (
                <div className="alert alert-danger d-flex align-items-center mb-3">
                  <i className="bi bi-exclamation-triangle-fill fs-4 me-3"></i>
                  <div>
                    <strong>Esta caja fue abierta hace {horasAbierta(cajaActiva)} horas</strong> (dia anterior).
                    Debe cerrarla lo antes posible. Las cajas abiertas se auto-cierran a las 21:00.
                  </div>
                </div>
              )}
              {/* ─── INFO CAJA ACTIVA ─── */}
              <div className="card border-0 shadow-sm mb-4">
                <div className="card-body">
                  <div className="row align-items-center">
                    <div className="col-md-3">
                      <div className="d-flex align-items-center gap-3">
                        <div className="d-flex align-items-center justify-content-center rounded-circle bg-success bg-opacity-10" style={{width:48,height:48}}>
                          <i className="bi bi-check-circle-fill text-success fs-4"></i>
                        </div>
                        <div>
                          <h5 className="mb-0 fw-bold text-success">Caja Abierta</h5>
                          {/* FIX: Safe access on cajaActiva.codigo */}
                          <small className="text-muted">{cajaActiva?.codigo || '—'}</small>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <small className="text-muted">Cajero</small>
                      <div className="fw-semibold">
                        <i className="bi bi-person me-1"></i>
                        {/* FIX: Safe access on cajaActiva name fields */}
                        {cajaActiva?.cajero_asignado_nombre || cajaActiva?.cajero_nombre || '—'}
                      </div>
                    </div>
                    <div className="col-md-3">
                      <small className="text-muted">Apertura</small>
                      <div className="fw-semibold">
                        <i className="bi bi-clock me-1"></i>
                        {/* FIX: Safe date rendering with fallback */}
                        {cajaActiva?.fecha_apertura ? new Date(cajaActiva.fecha_apertura).toLocaleString('es-EC', { hour:'2-digit', minute:'2-digit', day:'2-digit', month:'short' }) : '—'}
                      </div>
                    </div>
                    <div className="col-md-3">
                      {cajaActiva?.autorizado_por_nombre && (
                        <>
                          <small className="text-muted">Autorizado por</small>
                          <div className="fw-semibold">
                            <i className="bi bi-shield-check me-1 text-primary"></i>
                            {cajaActiva?.autorizado_por_nombre}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* ─── RESUMEN FINANCIERO ─── */}
              <div className="row g-3 mb-4">
                <div className="col-6 col-lg-3">
                  <div className="card border-0 shadow-sm h-100">
                    <div className="card-body text-center py-3">
                      <i className="bi bi-safe2 text-primary fs-4"></i>
                      <div className="text-muted small mt-1">Fondo Inicial</div>
                      {/* FIX: Safe access on cajaActiva.monto_apertura */}
                      <h4 className="fw-bold text-primary mb-0">{formatCurrency(cajaActiva?.monto_apertura || 0)}</h4>
                    </div>
                  </div>
                </div>
                <div className="col-6 col-lg-3">
                  <div className="card border-0 shadow-sm h-100">
                    <div className="card-body text-center py-3">
                      <i className="bi bi-bi bi-box-arrow-in-down text-success fs-4"></i>
                      <div className="text-muted small mt-1">Cobros en Caja</div>
                      <h4 className="fw-bold text-success mb-0">
                        {formatCurrency(cobrosEfectivo + cobrosNoEfectivo)}
                      </h4>
                      {cobrosNoEfectivo > 0 && (
                        <small className="text-muted d-block" style={{fontSize: '0.7rem'}}>
                          Efectivo {formatCurrency(cobrosEfectivo)} · Tarjeta/Transf. {formatCurrency(cobrosNoEfectivo)}
                        </small>
                      )}
                      <small className="text-muted d-block">
                        {cajaActiva?.resumen_ventas?.total_ventas || 0} ventas — Total {formatCurrency(cajaActiva?.resumen_ventas?.total_monto || 0)}
                      </small>
                      {parseFloat(cajaActiva?.resumen_servicios?.total_servicios || 0) > 0 && (
                        <small className="text-muted d-block" style={{fontSize: '0.7rem'}}>
                          + {cajaActiva?.resumen_servicios?.num_servicios || 0} servicios — {formatCurrency(cajaActiva?.resumen_servicios?.total_servicios || 0)}
                        </small>
                      )}
                      {(() => {
                        const ventasEfectivo = parseFloat(cajaActiva?.resumen_ventas?.efectivo || 0);
                        // Solo efectivo: ventas de contado en efectivo + abonos/otros ingresos en efectivo
                        const abonosRecibidos = parseFloat((cobrosEfectivo - ventasEfectivo).toFixed(2));
                        if (abonosRecibidos > 0) {
                          return (
                            <small className="text-muted d-block" style={{fontSize: '0.7rem'}}>
                              Efectivo: ventas {formatCurrency(ventasEfectivo)} + abonos/otros {formatCurrency(abonosRecibidos)}
                            </small>
                          );
                        }
                        return null;
                      })()}
                    </div>
                  </div>
                </div>
                <div className="col-6 col-lg-3">
                  <div className="card border-0 shadow-sm h-100">
                    <div className="card-body text-center py-3">
                      <i className="bi bi-box-arrow-up text-danger fs-4"></i>
                      <div className="text-muted small mt-1">Retiros / Egresos (efectivo)</div>
                      {/* Solo lo que sale del cajón, coherente con el efectivo esperado (backend) */}
                      <h4 className="fw-bold text-danger mb-0">
                        {formatCurrency(movTotales.salidas_efectivo || 0)}
                      </h4>
                    </div>
                  </div>
                </div>
                <div className="col-6 col-lg-3">
                  <div className="card border-0 shadow-sm h-100 border-primary">
                    <div className="card-body text-center py-3">
                      <i className="bi bi-cash-coin text-warning fs-4"></i>
                      <div className="text-muted small mt-1">Efectivo Esperado en Caja</div>
                      <h4 className="fw-bold mb-0">{formatCurrency(saldoEsperado)}</h4>
                      <small className="text-muted d-block" style={{fontSize: '0.7rem'}}>Solo billetes y monedas</small>
                    </div>
                  </div>
                </div>
              </div>

              {/* FIX: Safe access on cajaActiva.resumen_ventas fields */}
              {/* Desglose por metodo de pago */}
              <div className="row g-3 mb-4">
                {[
                  { label: 'Efectivo', value: cajaActiva?.resumen_ventas?.efectivo || 0, icon: 'bi-cash', color: 'success' },
                  { label: 'Tarjeta', value: cajaActiva?.resumen_ventas?.tarjeta || 0, icon: 'bi-credit-card', color: 'primary' },
                  { label: 'Transferencia', value: cajaActiva?.resumen_ventas?.transferencia || 0, icon: 'bi-bank', color: 'info' },
                  { label: 'Credito', value: cajaActiva?.resumen_ventas?.credito || 0, icon: 'bi-hourglass', color: 'warning' }
                ].map(item => (
                  <div key={item.label} className="col-6 col-md-3">
                    <div className="card bg-light border-0">
                      <div className="card-body py-2 text-center">
                        <i className={`bi ${item.icon} text-${item.color} me-1`}></i>
                        <small className="text-muted">{item.label}</small>
                        <div className="fw-bold">{formatCurrency(item.value || 0)}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* ─── ACCIONES ─── */}
              <div className="d-flex flex-wrap gap-2 mb-3">
                <button className="btn btn-outline-success" onClick={() => { setMovForm({ tipo: 'ingreso', concepto: '', monto: '', metodo_pago: 'efectivo' }); setShowMov(true); }}>
                  <i className="bi bi-plus-circle me-1"></i>Ingreso
                </button>
                <button className="btn btn-outline-warning" onClick={() => { setMovForm({ tipo: 'retiro', concepto: '', monto: '', metodo_pago: 'efectivo' }); setShowMov(true); }}>
                  <i className="bi bi-cash-stack me-1"></i>Retiro
                </button>
                {/* Egresos/gastos van por Caja Chica, no por caja registradora */}
                <div className="ms-auto">
                  <button className="btn btn-danger" onClick={openCerrarModal} disabled={modoEspectador || loadingValidacion}>
                    {loadingValidacion ? (
                      <><span className="spinner-border spinner-border-sm me-1"></span>Validando...</>
                    ) : (
                      <><i className="bi bi-lock me-1"></i>Cerrar Caja</>
                    )}
                  </button>
                </div>
              </div>

              {/* ─── MOVIMIENTOS ─── */}
              <div className="card border-0 shadow-sm">
                <div className="card-header bg-transparent">
                  <h6 className="mb-0 fw-bold">
                    <i className="bi bi-list-ul me-2"></i>
                    Movimientos del Período
                    <span className="badge bg-primary ms-2">{movimientos.length}</span>
                  </h6>
                </div>
                <div className="table-responsive">
                  <table className="table table-hover align-middle mb-0">
                    <thead className="bg-light">
                      <tr>
                        <th className="border-0">Hora</th>
                        <th className="border-0">Tipo</th>
                        <th className="border-0">Concepto</th>
                        <th className="border-0">Usuario</th>
                        <th className="border-0 text-end">Monto</th>
                      </tr>
                    </thead>
                    <tbody>
                      {movimientos.length === 0 ? (
                        <tr><td colSpan="5" className="text-center py-4 text-muted">Sin movimientos aún</td></tr>
                      ) : movimientos.map((m, i) => (
                        <tr key={i}>
                          {/* FIX: Safe date rendering with fallback */}
                          <td className="small">{m?.created_at ? new Date(m.created_at).toLocaleTimeString('es-EC', { hour: '2-digit', minute: '2-digit' }) : '—'}</td>
                          <td>
                            <span className={`badge bg-light text-dark`}>
                              <i className={`bi ${movIcon(m?.tipo)} me-1`}></i>
                              {(m?.tipo || '—').charAt(0).toUpperCase() + (m?.tipo || '—').slice(1)}
                            </span>
                          </td>
                          <td>{m?.concepto || '—'}</td>
                          <td className="small text-muted">{m?.usuario_nombre || '—'}</td>
                          <td className={`text-end fw-bold ${['venta','ingreso','fondo'].includes(m?.tipo) ? 'text-success' : 'text-danger'}`}>
                            {['venta','ingreso','fondo'].includes(m?.tipo) ? '+' : '-'}{formatCurrency(m?.monto || 0)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </>
      ) : (
        /* ─── HISTORIAL ─── */
        <div className="card border-0 shadow-sm">
          <div className="card-header bg-transparent">
            <h6 className="mb-0 fw-bold">
              <i className="bi bi-clock-history me-2"></i>Historial de Cajas
            </h6>
          </div>
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead className="bg-light">
                <tr>
                  <th className="border-0">Código</th>
                  <th className="border-0">Cajero</th>
                  <th className="border-0">Autorizado por</th>
                  <th className="border-0">Apertura</th>
                  <th className="border-0">Cierre</th>
                  <th className="border-0 text-end">Fondo</th>
                  <th className="border-0 text-end">Ventas</th>
                  <th className="border-0 text-end" title="Efectivo esperado en el cajón (cierres desde 18/10/2026; los anteriores incluían tarjeta/transferencia)">Esperado</th>
                  <th className="border-0 text-end">Real</th>
                  <th className="border-0 text-end">Diferencia</th>
                  <th className="border-0">Estado</th>
                  <th className="border-0"></th>
                </tr>
              </thead>
              <tbody>
                {historial.length === 0 ? (
                  <tr><td colSpan="12" className="text-center py-5 text-muted">
                    <i className="bi bi-clock-history display-6 d-block mb-2"></i>Sin historial
                  </td></tr>
                ) : historial.map(p => (
                  <tr key={p.id} className={esCajaZombie(p) ? 'table-danger' : ''}>
                    <td className="fw-semibold small">{p.codigo}</td>
                    <td className="small">{p.cajero_nombre}</td>
                    <td className="small">
                      {p.autorizado_por_nombre ? (
                        <><i className="bi bi-shield-check text-primary me-1"></i>{p.autorizado_por_nombre}</>
                      ) : <span className="text-muted">-</span>}
                    </td>
                    {/* FIX: null safety — guard date construction */}
                    <td className="small">{p.fecha_apertura ? new Date(p.fecha_apertura).toLocaleString('es-EC', { day:'2-digit', month:'2-digit', hour:'2-digit', minute:'2-digit' }) : '—'}</td>
                    <td className="small">{p.fecha_cierre ? new Date(p.fecha_cierre).toLocaleString('es-EC', { day:'2-digit', month:'2-digit', hour:'2-digit', minute:'2-digit' }) : '—'}</td>
                    <td className="text-end small">{formatCurrency(p.monto_apertura)}</td>
                    <td className="text-end fw-bold text-success small">{formatCurrency(p.total_ventas || 0)}</td>
                    <td className="text-end small">{p.monto_esperado ? formatCurrency(p.monto_esperado) : '—'}</td>
                    <td className="text-end small">{p.monto_real != null ? formatCurrency(p.monto_real) : '—'}</td>
                    <td className={`text-end fw-bold small ${parseFloat(p.diferencia || 0) < 0 ? 'text-danger' : parseFloat(p.diferencia || 0) > 0 ? 'text-warning' : 'text-success'}`}>
                      {p.diferencia != null ? formatCurrency(p.diferencia) : '—'}
                    </td>
                    <td>
                      {p.estado === 'abierta' && esCajaZombie(p) ? (
                        <>
                          <span className="badge bg-danger">
                            <i className="bi bi-exclamation-triangle-fill me-1"></i>Zombie
                          </span>
                          <div className="small text-danger mt-1">{horasAbierta(p)}h abierta</div>
                        </>
                      ) : (
                        <span className={`badge ${p.estado === 'abierta' ? 'bg-success' : 'bg-secondary'}`}>
                          {p.estado}
                        </span>
                      )}
                    </td>
                    <td>
                      <button className="btn btn-sm btn-outline-primary" onClick={() => verDetalle(p)} title="Ver detalle">
                        <i className="bi bi-eye"></i>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ═══ MODAL ABRIR CAJA (con autorización admin) ═══ */}
      {showAbrir && (
        <>
          <div className="modal-backdrop fade show" onClick={() => setShowAbrir(false)} />
          <div className="modal fade show d-block" tabIndex="-1">
            <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
              <div className="modal-content border-0 shadow">
                <div className="modal-header bg-primary text-white">
                  <h5 className="modal-title">
                    <i className="bi bi-unlock me-2"></i>Apertura de Caja
                  </h5>
                  <button className="btn-close btn-close-white" onClick={() => setShowAbrir(false)} />
                </div>
                <div className="modal-body">
                  {abrirError && (
                    <div className="alert alert-danger py-2">
                      <i className="bi bi-exclamation-triangle me-1"></i>{abrirError}
                    </div>
                  )}

                  {/* Autorización del Admin */}
                  {!esAdminOGerente && (
                    <div className="mb-4">
                      <div className="d-flex align-items-center gap-2 mb-3">
                        <div className="d-flex align-items-center justify-content-center rounded-circle bg-danger bg-opacity-10" style={{width:36,height:36}}>
                          <i className="bi bi-shield-lock text-danger"></i>
                        </div>
                        <div>
                          <h6 className="mb-0 fw-bold">Autorización Requerida</h6>
                          <small className="text-muted">Un Administrador o Gerente debe autorizar esta apertura</small>
                        </div>
                      </div>
                      <div className="row g-3">
                        <div className="col-md-6">
                          <label className="form-label small fw-semibold">Usuario del Autorizador *</label>
                          <div className="input-group">
                            <span className="input-group-text"><i className="bi bi-person-badge"></i></span>
                            <input
                              type="text" className="form-control"
                              placeholder="admin"
                              value={abrirForm.admin_username}
                              onChange={e => setAbrirForm({...abrirForm, admin_username: e.target.value})}
                              autoFocus
                            />
                          </div>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label small fw-semibold">Contraseña del Autorizador *</label>
                          <div className="input-group">
                            <span className="input-group-text"><i className="bi bi-key"></i></span>
                            <input
                              type="password" className="form-control"
                              placeholder="••••••"
                              value={abrirForm.admin_password}
                              onChange={e => setAbrirForm({...abrirForm, admin_password: e.target.value})}
                            />
                          </div>
                        </div>
                      </div>
                      <hr />
                    </div>
                  )}

                  {/* Asignar Cajero */}
                  {esAdminOGerente && cajeros.length > 0 && (
                    <div className="mb-3">
                      <label className="form-label small fw-semibold">
                        <i className="bi bi-person-workspace me-1"></i>Asignar Cajero
                      </label>
                      <select
                        className="form-select"
                        value={abrirForm.cajero_id}
                        onChange={e => setAbrirForm({...abrirForm, cajero_id: e.target.value})}
                      >
                        {cajeros.map(c => (
                          <option key={c.id} value={c.id}>
                            {c.nombre_completo} ({c.rol_nombre})
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* Fondo Inicial */}
                  <div className="mb-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <label className="form-label small fw-semibold mb-0">
                        <i className="bi bi-cash-coin me-1"></i>Fondo Inicial (Efectivo en Caja)
                      </label>
                      <div className="form-check form-switch">
                        <input
                          type="checkbox" className="form-check-input" id="usarDenom"
                          checked={usarDenominaciones}
                          onChange={e => setUsarDenominaciones(e.target.checked)}
                        />
                        <label className="form-check-label small" htmlFor="usarDenom">
                          Contar por denominación
                        </label>
                      </div>
                    </div>

                    {usarDenominaciones ? (
                      <DenomCounter
                        values={abrirForm.fondo_denominaciones}
                        onChange={v => setAbrirForm({...abrirForm, fondo_denominaciones: v})}
                      />
                    ) : (
                      <div className="input-group input-group-lg">
                        <span className="input-group-text">$</span>
                        <input
                          type="number" className="form-control" placeholder="0.00" step="0.01" min="0"
                          value={abrirForm.monto_apertura}
                          onChange={e => setAbrirForm({...abrirForm, monto_apertura: e.target.value})}
                        />
                      </div>
                    )}
                  </div>

                  {/* Observaciones */}
                  <div className="mb-3">
                    <label className="form-label small fw-semibold">Observaciones</label>
                    <textarea
                      className="form-control" rows="2"
                      value={abrirForm.observaciones}
                      onChange={e => setAbrirForm({...abrirForm, observaciones: e.target.value})}
                      placeholder="Notas de apertura (opcional)"
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <button className="btn btn-light" onClick={() => setShowAbrir(false)} disabled={saving}>
                    Cancelar
                  </button>
                  <button className="btn btn-primary" onClick={handleAbrir} disabled={saving}>
                    {saving ? (
                      <><span className="spinner-border spinner-border-sm me-1"></span> Abriendo...</>
                    ) : (
                      <><i className="bi bi-unlock me-1"></i>Abrir Caja</>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ═══ MODAL VALIDACIONES PRE-CIERRE ═══ */}
      {showValidaciones && validaciones && (
        <>
          <div className="modal-backdrop fade show" onClick={() => setShowValidaciones(false)} />
          <div className="modal fade show d-block" tabIndex="-1">
            <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
              <div className="modal-content border-0 shadow">
                <div className="modal-header bg-danger text-white">
                  <h5 className="modal-title"><i className="bi bi-shield-exclamation me-2"></i>No se puede cerrar la caja</h5>
                  <button className="btn-close btn-close-white" onClick={() => setShowValidaciones(false)} />
                </div>
                <div className="modal-body">
                  <div className="alert alert-danger py-2 mb-3">
                    <i className="bi bi-exclamation-octagon me-1"></i>
                    <strong>Se encontraron problemas que deben resolverse antes de cerrar la caja.</strong>
                  </div>

                  {validaciones.validaciones?.gastos_pendientes && !validaciones.validaciones.gastos_pendientes.ok && (
                    <div className="card border-warning mb-3">
                      <div className="card-header bg-warning bg-opacity-10">
                        <i className="bi bi-hourglass-split text-warning me-2"></i>
                        <strong>{validaciones.validaciones.gastos_pendientes.mensaje}</strong>
                      </div>
                      {validaciones.validaciones.gastos_pendientes.detalle?.length > 0 && (
                        <div className="card-body p-0">
                          <table className="table table-sm mb-0">
                            <thead><tr><th>Código</th><th>Concepto</th><th className="text-end">Monto</th></tr></thead>
                            <tbody>
                              {validaciones.validaciones.gastos_pendientes.detalle.map(g => (
                                <tr key={g.id}><td><code>{g.codigo}</code></td><td>{g.concepto}</td><td className="text-end">{formatCurrency(g.monto)}</td></tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}

                  {validaciones.validaciones?.gastos_sin_comprobante && !validaciones.validaciones.gastos_sin_comprobante.ok && (
                    <div className="card border-danger mb-3">
                      <div className="card-header bg-danger bg-opacity-10">
                        <i className="bi bi-file-earmark-x text-danger me-2"></i>
                        <strong>{validaciones.validaciones.gastos_sin_comprobante.mensaje}</strong>
                      </div>
                      {validaciones.validaciones.gastos_sin_comprobante.detalle?.length > 0 && (
                        <div className="card-body p-0">
                          <table className="table table-sm mb-0">
                            <thead><tr><th>Código</th><th>Concepto</th><th className="text-end">Monto</th></tr></thead>
                            <tbody>
                              {validaciones.validaciones.gastos_sin_comprobante.detalle.map(g => (
                                <tr key={g.id}><td><code>{g.codigo}</code></td><td>{g.concepto}</td><td className="text-end">{formatCurrency(g.monto)}</td></tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}

                  {validaciones.validaciones?.stock_vs_ventas && !validaciones.validaciones.stock_vs_ventas.ok && (
                    <div className="card border-info mb-3">
                      <div className="card-header bg-info bg-opacity-10">
                        <i className="bi bi-box-seam text-info me-2"></i>
                        <strong>{validaciones.validaciones.stock_vs_ventas.mensaje}</strong>
                      </div>
                      {validaciones.validaciones.stock_vs_ventas.detalle?.length > 0 && (
                        <div className="card-body p-0">
                          <table className="table table-sm mb-0">
                            <thead><tr><th>Producto</th><th className="text-center">Esperado</th><th className="text-center">Real</th><th className="text-center">Diferencia</th></tr></thead>
                            <tbody>
                              {validaciones.validaciones.stock_vs_ventas.detalle.map(p => (
                                <tr key={p.producto_id}>
                                  <td>{p.nombre} <code className="small">{p.codigo}</code></td>
                                  <td className="text-center">{p.stock_esperado}</td>
                                  <td className="text-center">{p.stock_real}</td>
                                  <td className={`text-center fw-bold ${p.diferencia < 0 ? 'text-danger' : 'text-warning'}`}>{p.diferencia}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  <button className="btn btn-secondary" onClick={() => setShowValidaciones(false)}>Entendido</button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ═══ MODAL CERRAR CAJA — ARQUEO CIEGO ═══ */}
      {showCerrar && (
        <>
          <div className="modal-backdrop fade show" onClick={() => setShowCerrar(false)} />
          <div className="modal fade show d-block" tabIndex="-1">
            <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
              <div className="modal-content border-0 shadow">
                <div className="modal-header bg-danger text-white">
                  <h5 className="modal-title"><i className="bi bi-lock me-2"></i>Cierre de Caja — Arqueo</h5>
                  <button className="btn-close btn-close-white" onClick={() => setShowCerrar(false)} />
                </div>
                <div className="modal-body">
                  {cerrarStep === 'conteo' ? (
                    <>
                      {/* PASO 1: Conteo ciego — NO mostrar monto esperado */}
                      <div className="alert alert-primary py-2 mb-4">
                        <i className="bi bi-eye-slash me-2"></i>
                        <strong>Arqueo ciego:</strong> Cuente el efectivo en caja y registre el total. El monto esperado se revelará después.
                      </div>

                      {/* Credenciales admin — solo para no-admins */}
                      {!esAdmin() && (
                        <div className="card border-warning mb-4">
                          <div className="card-body py-3">
                            <h6 className="card-title text-warning mb-3">
                              <i className="bi bi-shield-lock me-2"></i>Autorización de Administrador
                            </h6>
                            <div className="row g-2">
                              <div className="col-6">
                                <label className="form-label small fw-semibold">Usuario Admin</label>
                                <input
                                  type="text" className="form-control"
                                  placeholder="Username del admin"
                                  value={cerrarForm.admin_username}
                                  onChange={e => setCerrarForm({...cerrarForm, admin_username: e.target.value})}
                                />
                              </div>
                              <div className="col-6">
                                <label className="form-label small fw-semibold">Contraseña</label>
                                <input
                                  type="password" className="form-control"
                                  placeholder="Contraseña del admin"
                                  value={cerrarForm.admin_password}
                                  onChange={e => setCerrarForm({...cerrarForm, admin_password: e.target.value})}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Advertencia de discrepancias de stock si hay */}
                      {validaciones?.validaciones?.stock_vs_ventas && !validaciones.validaciones.stock_vs_ventas.ok && (
                        <div className="alert alert-warning py-2 mb-3">
                          <i className="bi bi-box-seam me-1"></i>
                          <strong>Advertencia:</strong> {validaciones.validaciones.stock_vs_ventas.mensaje}
                          <small className="d-block mt-1 text-muted">
                            {validaciones.validaciones.stock_vs_ventas.detalle?.length} producto(s) con discrepancia de stock.
                          </small>
                        </div>
                      )}

                      {/* Qué se cuenta: solo el efectivo del cajón (auditoría 2026-10-18) */}
                      <div className="alert alert-info py-2 small mb-3">
                        <i className="bi bi-info-circle me-1"></i>
                        Cuente <strong>solo billetes y monedas</strong>.
                        {cobrosNoEfectivo > 0 && (
                          <> Lo cobrado con tarjeta o transferencia en este turno ({formatCurrency(cobrosNoEfectivo)}) <strong>no</strong> está en el cajón y no se cuenta.</>
                        )}
                      </div>

                      {/* Conteo de efectivo */}
                      <div className="mb-3">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <label className="form-label fw-semibold mb-0">
                            <i className="bi bi-cash-coin me-1"></i>Conteo de Efectivo en Caja
                          </label>
                          <div className="form-check form-switch">
                            <input
                              type="checkbox" className="form-check-input" id="usarDenomCierre"
                              checked={usarDenomCierre}
                              onChange={e => setUsarDenomCierre(e.target.checked)}
                            />
                            <label className="form-check-label small" htmlFor="usarDenomCierre">
                              Contar por denominación
                            </label>
                          </div>
                        </div>

                        {usarDenomCierre ? (
                          <DenomCounter
                            values={cerrarForm.cierre_denominaciones}
                            onChange={v => setCerrarForm({...cerrarForm, cierre_denominaciones: v})}
                          />
                        ) : (
                          <div className="input-group input-group-lg">
                            <span className="input-group-text">$</span>
                            <input
                              type="number" className="form-control" placeholder="0.00" step="0.01" min="0"
                              value={cerrarForm.monto_real}
                              onChange={e => setCerrarForm({...cerrarForm, monto_real: e.target.value})}
                              autoFocus
                            />
                          </div>
                        )}
                      </div>

                      {/* Observaciones */}
                      <div className="mb-3">
                        <label className="form-label small fw-semibold">Observaciones del Cierre</label>
                        <textarea
                          className="form-control" rows="2"
                          value={cerrarForm.observaciones}
                          onChange={e => setCerrarForm({...cerrarForm, observaciones: e.target.value})}
                          placeholder="Notas del turno, observaciones generales..."
                        />
                      </div>
                    </>
                  ) : (
                    <>
                      {/* PASO 2: Resultado — REVELAR diferencia */}
                      {(() => {
                        const montoReal = getMontoContado();
                        const diff = montoReal - saldoEsperado;
                        const diffAbs = Math.abs(diff);
                        const isOk = diffAbs <= 2;

                        return (
                          <>
                            <div className={`alert ${isOk ? 'alert-success' : diff < 0 ? 'alert-danger' : 'alert-warning'} mb-4`}>
                              <h6 className="alert-heading mb-3">
                                {isOk ? (
                                  <><i className="bi bi-check-circle-fill me-2"></i>Arqueo correcto</>
                                ) : diff < 0 ? (
                                  <><i className="bi bi-exclamation-triangle-fill me-2"></i>Faltante detectado</>
                                ) : (
                                  <><i className="bi bi-exclamation-triangle-fill me-2"></i>Sobrante detectado</>
                                )}
                              </h6>
                              <div className="row text-center">
                                <div className="col-4">
                                  <small className="text-muted">Efectivo esperado</small>
                                  <div className="fw-bold fs-5">{formatCurrency(saldoEsperado)}</div>
                                </div>
                                <div className="col-4">
                                  <small className="text-muted">Contado</small>
                                  <div className="fw-bold fs-5">{formatCurrency(montoReal)}</div>
                                </div>
                                <div className="col-4">
                                  <small className="text-muted">Diferencia</small>
                                  <div className="fw-bold fs-5">
                                    {diff > 0 ? '+' : ''}{formatCurrency(diff)}
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Justificación obligatoria si diferencia > $2 */}
                            {diffAbs > 2 && (
                              <div className="mb-3">
                                <label className="form-label fw-semibold text-danger">
                                  <i className="bi bi-exclamation-triangle me-1"></i>
                                  Justificación de la diferencia (obligatorio) *
                                </label>
                                <textarea
                                  className="form-control border-danger"
                                  rows="3"
                                  value={cerrarForm.justificacion_diferencia}
                                  onChange={e => setCerrarForm({...cerrarForm, justificacion_diferencia: e.target.value})}
                                  placeholder="Explique la razón de la diferencia de efectivo..."
                                  autoFocus
                                />
                                <div className="form-text text-danger">
                                  La diferencia supera $2.00. Debe justificar antes de cerrar.
                                </div>
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </>
                  )}
                </div>
                <div className="modal-footer">
                  {cerrarStep === 'conteo' ? (
                    <>
                      <button className="btn btn-light" onClick={() => setShowCerrar(false)} disabled={saving}>Cancelar</button>
                      <button className="btn btn-danger" onClick={handleCerrarStep1}>
                        <i className="bi bi-arrow-right me-1"></i>Verificar Arqueo
                      </button>
                    </>
                  ) : (
                    <>
                      <button className="btn btn-light" onClick={() => setCerrarStep('conteo')} disabled={saving}>
                        <i className="bi bi-arrow-left me-1"></i>Volver al Conteo
                      </button>
                      <button className="btn btn-danger" onClick={handleCerrar} disabled={saving}>
                        {saving ? (
                          <><span className="spinner-border spinner-border-sm me-1"></span> Cerrando...</>
                        ) : (
                          <><i className="bi bi-lock me-1"></i>Confirmar Cierre</>
                        )}
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ═══ MODAL MOVIMIENTO ═══ */}
      {showMov && (
        <>
          <div className="modal-backdrop fade show" onClick={() => setShowMov(false)} />
          <div className="modal fade show d-block" tabIndex="-1">
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content border-0 shadow">
                <div className="modal-header">
                  <h5 className="modal-title">
                    <i className={`bi ${movIcon(movForm.tipo)} me-2`}></i>
                    Registrar {movForm.tipo.charAt(0).toUpperCase() + movForm.tipo.slice(1)}
                  </h5>
                  <button className="btn-close" onClick={() => setShowMov(false)} />
                </div>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label fw-semibold">Motivo / Concepto *</label>
                    <textarea className="form-control" rows="2"
                           placeholder="Describa detalladamente el motivo del movimiento (mín. 5 caracteres)"
                           value={movForm.concepto}
                           onChange={e => setMovForm({...movForm, concepto: e.target.value})}
                           autoFocus />
                    <small className="text-muted">{(movForm.concepto || '').length}/5 caracteres mínimo</small>
                  </div>
                  <div className="mb-3">
                    <label className="form-label fw-semibold">Monto *</label>
                    <div className="input-group">
                      <span className="input-group-text">$</span>
                      <input type="number" className="form-control" placeholder="0.00" step="0.01" min="0.01"
                             value={movForm.monto}
                             onChange={e => setMovForm({...movForm, monto: e.target.value})} />
                    </div>
                  </div>
                  {/* Adjuntos opcionales */}
                  <div className="mb-2">
                    <label className="form-label fw-semibold d-flex justify-content-between">
                      <span><i className="bi bi-paperclip me-1"></i>Comprobantes (opcional)</span>
                      <span className="badge bg-secondary">{movAdjuntos.length}/6</span>
                    </label>
                    <input ref={movAdjuntosRef} type="file" className="d-none" multiple
                      accept="image/jpeg,image/png,image/gif,image/webp,application/pdf"
                      onChange={handleMovAdjuntosChange} />
                    {movAdjuntos.length === 0 ? (
                      <div className="border rounded p-3 text-center" style={{borderStyle:'dashed', cursor:'pointer', backgroundColor:'#f8f9fa'}}
                        onClick={() => movAdjuntosRef.current?.click()}>
                        <i className="bi bi-cloud-arrow-up fs-4 text-muted d-block mb-1"></i>
                        <small className="text-muted">Clic para adjuntar comprobantes (foto o PDF, máx. 6)</small>
                      </div>
                    ) : (
                      <>
                        {movAdjuntos.map((file, idx) => (
                          <div key={idx} className="d-flex align-items-center gap-2 border rounded py-1 px-2 mb-1">
                            {file.type === 'application/pdf' ? <i className="bi bi-file-pdf text-danger"></i> :
                              <img src={URL.createObjectURL(file)} alt="" style={{width:24,height:24,objectFit:'cover',borderRadius:3}} />}
                            <span className="small text-truncate flex-grow-1">{file.name}</span>
                            <span className="text-muted" style={{fontSize:'0.7rem'}}>{(file.size/1024).toFixed(0)}KB</span>
                            <button type="button" className="btn btn-sm btn-outline-danger py-0 px-1" onClick={() => setMovAdjuntos(prev => prev.filter((_,i) => i !== idx))}><i className="bi bi-x"></i></button>
                          </div>
                        ))}
                        {movAdjuntos.length < 6 && (
                          <button type="button" className="btn btn-sm btn-outline-primary mt-1" onClick={() => movAdjuntosRef.current?.click()}>
                            <i className="bi bi-plus me-1"></i>Agregar más
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>
                <div className="modal-footer">
                  <button className="btn btn-light" onClick={() => { setShowMov(false); setMovAdjuntos([]); }} disabled={saving}>Cancelar</button>
                  <button className="btn btn-primary" onClick={handleMovimiento} disabled={saving || !movForm.concepto?.trim() || (movForm.concepto?.trim().length || 0) < 5}>
                    {saving ? <span className="spinner-border spinner-border-sm"></span> : <><i className="bi bi-check-lg me-1"></i>Registrar</>}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ═══ MODAL DETALLE PERIODO ═══ */}
      {showDetalle && periodoDetalle && (
        <>
          <div className="modal-backdrop fade show" onClick={() => setShowDetalle(false)} />
          <div className="modal fade show d-block" tabIndex="-1">
            <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
              <div className="modal-content border-0 shadow">
                <div className="modal-header">
                  <h5 className="modal-title">
                    <i className="bi bi-safe me-2"></i>
                    Período {periodoDetalle.codigo}
                  </h5>
                  <button className="btn-close" onClick={() => setShowDetalle(false)} />
                </div>
                <div className="modal-body">
                  <div className="row g-3 mb-3">
                    <div className="col-6 col-md-3 text-center">
                      <small className="text-muted">Fondo</small>
                      <div className="fw-bold">{formatCurrency(periodoDetalle.monto_apertura)}</div>
                    </div>
                    <div className="col-6 col-md-3 text-center">
                      <small className="text-muted">Total Ventas</small>
                      <div className="fw-bold text-success">{formatCurrency(periodoDetalle.total_ventas || 0)}</div>
                    </div>
                    <div className="col-6 col-md-3 text-center">
                      <small className="text-muted">Monto Real</small>
                      <div className="fw-bold">{periodoDetalle.monto_real != null ? formatCurrency(periodoDetalle.monto_real) : '—'}</div>
                    </div>
                    <div className="col-6 col-md-3 text-center">
                      <small className="text-muted">Diferencia</small>
                      <div className={`fw-bold ${parseFloat(periodoDetalle.diferencia || 0) < 0 ? 'text-danger' : 'text-success'}`}>
                        {periodoDetalle.diferencia != null ? formatCurrency(periodoDetalle.diferencia) : '—'}
                      </div>
                    </div>
                  </div>

                  {periodoDetalle.autorizado_por_nombre && (
                    <div className="alert alert-light py-2 mb-3">
                      <i className="bi bi-shield-check text-primary me-1"></i>
                      <strong>Autorizado por:</strong> {periodoDetalle.autorizado_por_nombre}
                    </div>
                  )}

                  <div className="table-responsive">
                    <table className="table table-sm table-hover mb-0">
                      <thead className="bg-light">
                        <tr>
                          <th>Hora</th><th>Tipo</th><th>Concepto</th>
                          <th>Usuario</th><th className="text-end">Monto</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(movsPeriodo || []).map((m, i) => (
                          <tr key={i}>
                            <td className="small">{m.created_at ? new Date(m.created_at).toLocaleTimeString('es-EC', { hour:'2-digit', minute:'2-digit' }) : '—'}</td>
                            <td><i className={`bi ${movIcon(m.tipo)} me-1`}></i>{m.tipo || '—'}</td>
                            <td>{m.concepto}</td>
                            <td className="small text-muted">{m.usuario_nombre}</td>
                            <td className={`text-end fw-bold ${['venta','ingreso','fondo'].includes(m.tipo) ? 'text-success' : 'text-danger'}`}>
                              {['venta','ingreso','fondo'].includes(m.tipo) ? '+' : '-'}{formatCurrency(m.monto)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                <div className="modal-footer">
                  <button className="btn btn-light" onClick={() => setShowDetalle(false)}>Cerrar</button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
      {/* ═══ MODAL CERRAR PERIODO ═══ */}
      {showCerrarPeriodo && periodoActivo && (
        <>
          <div className="modal-backdrop fade show" onClick={() => setShowCerrarPeriodo(false)} />
          <div className="modal fade show d-block" tabIndex="-1">
            <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
              <div className="modal-content border-0 shadow">
                <div className="modal-header bg-danger text-white">
                  <h5 className="modal-title">
                    <i className="bi bi-stop-circle me-2"></i>Cierre de Periodo — {periodoActivo.codigo}
                  </h5>
                  <button className="btn-close btn-close-white" onClick={() => setShowCerrarPeriodo(false)} />
                </div>
                <div className="modal-body">
                  {cajasDelPeriodo.filter(c => c.estado === 'abierta').length > 0 && (
                    <div className="alert alert-warning">
                      <i className="bi bi-exclamation-triangle me-1"></i>
                      <strong>Atencion:</strong> Hay {cajasDelPeriodo.filter(c => c.estado === 'abierta').length} caja(s) aun abiertas.
                      Cierre todas las cajas individuales antes de cerrar el periodo.
                      <div className="mt-2">
                        {cajasDelPeriodo.filter(c => c.estado === 'abierta').map(c => (
                          <span key={c.id} className="badge bg-warning text-dark me-2">
                            <i className="bi bi-person me-1"></i>{c.cajero_asignado_nombre || c.cajero_nombre}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  <h6 className="fw-bold mb-3"><i className="bi bi-table me-2"></i>Resumen Consolidado por Cajero</h6>
                  <div className="table-responsive">
                    <table className="table table-sm table-bordered align-middle mb-3">
                      <thead className="bg-light">
                        <tr>
                          <th>Cajero</th>
                          <th className="text-center">Ventas</th>
                          <th className="text-end">Monto Ventas</th>
                          <th className="text-end">Apertura</th>
                          <th className="text-end" title="Todos los métodos de pago">Ingresos (todos)</th>
                          <th className="text-end">Retiros</th>
                          <th className="text-end" title="Efectivo esperado en el cajón (cierres desde 18/10/2026; los anteriores incluían tarjeta/transferencia)">Esperado</th>
                          <th className="text-end">Real</th>
                          <th className="text-end">Diferencia</th>
                          <th className="text-center">Estado</th>
                        </tr>
                      </thead>
                      <tbody>
                        {cajasDelPeriodo.map(c => (
                          <tr key={c.id}>
                            <td className="fw-semibold">{c.cajero_asignado_nombre || c.cajero_nombre}</td>
                            <td className="text-center">{c.num_ventas || 0}</td>
                            <td className="text-end fw-bold text-success">{formatCurrency(c.monto_ventas || c.total_ventas || 0)}</td>
                            <td className="text-end">{formatCurrency(c.monto_apertura || 0)}</td>
                            <td className="text-end">{formatCurrency(c.total_ingresos || 0)}</td>
                            <td className="text-end">{formatCurrency(c.total_retiros || 0)}</td>
                            <td className="text-end">{c.monto_esperado != null ? formatCurrency(c.monto_esperado) : '—'}</td>
                            <td className="text-end">{c.monto_real != null ? formatCurrency(c.monto_real) : '—'}</td>
                            <td className={`text-end fw-bold ${parseFloat(c.diferencia || 0) < 0 ? 'text-danger' : parseFloat(c.diferencia || 0) > 0 ? 'text-warning' : 'text-success'}`}>
                              {c.diferencia != null ? formatCurrency(c.diferencia) : '—'}
                            </td>
                            <td className="text-center">
                              <span className={`badge ${c.estado === 'abierta' ? 'bg-success' : 'bg-secondary'}`}>{c.estado}</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="bg-light fw-bold">
                        <tr>
                          <td>TOTALES</td>
                          <td className="text-center">{cajasDelPeriodo.reduce((s, c) => s + parseInt(c.num_ventas || 0), 0)}</td>
                          <td className="text-end text-success">{formatCurrency(cajasDelPeriodo.reduce((s, c) => s + parseFloat(c.monto_ventas || c.total_ventas || 0), 0))}</td>
                          <td className="text-end">{formatCurrency(cajasDelPeriodo.reduce((s, c) => s + parseFloat(c.monto_apertura || 0), 0))}</td>
                          <td className="text-end">{formatCurrency(cajasDelPeriodo.reduce((s, c) => s + parseFloat(c.total_ingresos || 0), 0))}</td>
                          <td className="text-end">{formatCurrency(cajasDelPeriodo.reduce((s, c) => s + parseFloat(c.total_retiros || 0), 0))}</td>
                          <td className="text-end">{formatCurrency(cajasDelPeriodo.filter(c => c.monto_esperado != null).reduce((s, c) => s + parseFloat(c.monto_esperado || 0), 0))}</td>
                          <td className="text-end">{formatCurrency(cajasDelPeriodo.filter(c => c.monto_real != null).reduce((s, c) => s + parseFloat(c.monto_real || 0), 0))}</td>
                          <td className={`text-end ${cajasDelPeriodo.reduce((s, c) => s + parseFloat(c.diferencia || 0), 0) < 0 ? 'text-danger' : 'text-success'}`}>
                            {formatCurrency(cajasDelPeriodo.filter(c => c.diferencia != null).reduce((s, c) => s + parseFloat(c.diferencia || 0), 0))}
                          </td>
                          <td></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>

                  <div className="mb-3">
                    <label className="form-label fw-semibold">Observaciones del Cierre</label>
                    <textarea className="form-control" rows="2" placeholder="Observaciones opcionales..."
                      value={obsCierrePeriodo} onChange={e => setObsCierrePeriodo(e.target.value)} />
                  </div>
                </div>
                <div className="modal-footer">
                  <button className="btn btn-light" onClick={() => setShowCerrarPeriodo(false)} disabled={saving}>Cancelar</button>
                  <button className="btn btn-danger" onClick={handleCerrarPeriodo}
                    disabled={saving || cajasDelPeriodo.filter(c => c.estado === 'abierta').length > 0}>
                    {saving ? (
                      <><span className="spinner-border spinner-border-sm me-1"></span>Cerrando...</>
                    ) : (
                      <><i className="bi bi-stop-circle me-1"></i>Confirmar Cierre de Periodo</>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Caja;
